import React, { useCallback, useRef, useState } from "react";
import { WorkInstruction as WorkInstructionEntity} from "../../types/api/work-instruction/get-work-instructions-success-response";
import './work-instruction.css';
import { getImageUrl } from "../../../../utils/image-helper/get-image-url";
import { networkCall } from "../../../../utils/network-call/network-call";
import { Textarea } from "../../../../components/field/textarea/textarea";
import { AlertMessageState, clearAlertMessage, showAlertMessage } from "../../../../redux/reducer/alert-message.reducer";
import { useDispatch } from "react-redux";
import { AlertMessages } from "../../../../enums/alert-messages.enum";
import { showLoadingAlertMessage } from "../../../../utils/show-loading-alert-message/show-loading-alert-message";
import { showDefaultErrorMessages } from "../../../../utils/show-default-error-message/show-default-error-messages";
import { AxiosResponse } from "axios";
import { DefaultErrorResponse } from "../../../../types/api/error/default-error-response/default-error-respose.type";
import { ValidationErrorResponseData } from "../../../../types/api/error/validation-error-response-data/validation-error-response-data.type.";
import { useToggle } from "../../../../hooks/use-toggle";
import { AudioUploader } from "../file-uploaders/audio-uploader";
import { VideoUploader } from "../file-uploaders/video-uploader";
import { ImageUploader } from "../file-uploaders/image-uploader";
import { ModelUploader } from "../file-uploaders/model-uploader";
import { PDFUploader } from "../file-uploaders/pdf-uploader";
import useDebouncedFunction from "../../../../hooks/use-debounced-function";
import { getVideoUrl } from "../../../../utils/get-video-url/get-video-url";
import { usePreventScrollWhenModalIsOpen } from "../../../../hooks/use-prevent-scroll-when-modal-is-open";
import { IOTDeviceModal, IOTDeviceModalType } from "../iot-device-modal/iot-device-modal";

type WorkInstructionProps = {
    selectedWorkInstructionIndex: number | null;
    workInstruction: WorkInstructionEntity;
    index: number;
    onWorkInstructionClick: (index: number) => void;
    onDelete: () => void;
    onEdit: () => void;
    onClose: () => void;
    onNewIOTDeviceAdd?: () => void;
    onIOTDeviceEdit?: () => void;
    onIOTDeviceDelete?: () => void;
    onSavedWorkInstruction: (editedWorkInstruction: WorkInstructionEntity) => void;
    onAnimationStart?: () => void;
    onAnimationEnd?: () => void;
};

export const WorkInstruction = ({
    selectedWorkInstructionIndex,
    workInstruction,
    onWorkInstructionClick,
    index,
    onDelete,
    onEdit,
    onClose,
    onNewIOTDeviceAdd,
    onIOTDeviceEdit,
    onIOTDeviceDelete,
    onSavedWorkInstruction,
    onAnimationEnd,
    onAnimationStart
}: WorkInstructionProps) => {
    const dispatch = useDispatch();
    const [isActive, setIsActive] = useState(false);
    const [audioInstruction, setAudioInstruction] = useState(workInstruction.audioInstruction === null ? '' : workInstruction.audioInstruction);
    const [isIOTDeviceModalOpen, toggleIOTDeviceModalOpen] = useToggle(false);
    const [iotDeviceModalType, setIOTDeviceModalType] = useState<IOTDeviceModalType>('newIOTDevice');
    const [iotDevicesHandlingVisible, toggleIOTDevicesHandlingVisible] = useToggle(false);
    const [selectedIOTDeviceId, setSelectedIOTDeviceId] = useState<number | null>(null);
    const selectedIOTDevice = workInstruction.iotDevices.find(device => device.id === selectedIOTDeviceId);
    const [iotDeviceModalKey, setIOTDeviceModalKey] = useState(0);
    const [workInsructionData, setWorkInsructionData] = useState<{ title: string; description: string }>({
        title: workInstruction.title,
        description: workInstruction.description
    });
    const [isWorkInsructionDataEditable, toggleIsWorkInsructionDataEditable] = useToggle(false);
    const [prevSelectedWorkInstructionIndex, setPrevSelectedWorkInstructionIndex] = useState(selectedWorkInstructionIndex);
    const [inputsContainerClass, setInputsContainerClass] = useState('d-none');
    const [buttonsContainerClass, setButtonsContainerClass] = useState('d-none');
    const animationVideoRef = useRef<HTMLVideoElement>();

    usePreventScrollWhenModalIsOpen(isIOTDeviceModalOpen);

    async function showAnimation() {
        if (animationVideoRef.current) {
            try {
                if (onAnimationStart) onAnimationStart();

                animationVideoRef.current.currentTime = 0.3;
                animationVideoRef.current.playbackRate = 2.5;
                await animationVideoRef.current.play();

                setIsActive(true);
                setButtonsContainerClass('d-none');
                setInputsContainerClass('d-none');

                setTimeout(() => {
                    animationVideoRef.current?.pause();

                    setButtonsContainerClass('d-block animate__faster animate__fadeIn');
                    setInputsContainerClass('d-block animate__faster animate__fadeIn');

                    setTimeout(() => { if (onAnimationEnd) onAnimationEnd(); }, 500);
                }, 2000 / 2.5);
            } catch (error) {
                if (onAnimationEnd) onAnimationEnd();
            }
        }
    }

    async function hideAnimation() {
        if (animationVideoRef.current) {
            try {
                if (onAnimationStart) onAnimationStart();

                animationVideoRef.current.currentTime = 2.4;
                animationVideoRef.current.playbackRate = 2.5;
                await animationVideoRef.current.play();

                setTimeout(() => {
                    animationVideoRef.current?.pause();

                    setIsActive(false);

                    if (onAnimationEnd) onAnimationEnd();
                }, 3300 / 2.5);

                setButtonsContainerClass('d-block animate__faster animate__fadeOut');
                setInputsContainerClass('d-block animate__faster animate__fadeOut');
                setTimeout(() => {
                    setInputsContainerClass('d-none');
                    setButtonsContainerClass('d-none');
                }, 500);
            } catch (error) {
                if (onAnimationEnd) onAnimationEnd();
            }
        }
    }

    if (selectedWorkInstructionIndex !== prevSelectedWorkInstructionIndex) {
        setPrevSelectedWorkInstructionIndex(selectedWorkInstructionIndex);

        if (prevSelectedWorkInstructionIndex === null && selectedWorkInstructionIndex === index) {
            showAnimation();
        } else if (prevSelectedWorkInstructionIndex !== null && selectedWorkInstructionIndex === index) {
            
            if (prevSelectedWorkInstructionIndex - 1 === index || prevSelectedWorkInstructionIndex + 1 === index) {
                setTimeout(() => showAnimation(), 1100);
            } else {
                showAnimation();
            }
            
        } else if (prevSelectedWorkInstructionIndex === index) {
            hideAnimation();
        }
    }

    if (isActive === false && isWorkInsructionDataEditable === true) {
        toggleIsWorkInsructionDataEditable();
        setWorkInsructionData({
            title: workInstruction.title,
            description: workInstruction.description
        });
    }

    const handleWorkInstructionClick = () => {
        onWorkInstructionClick(index);
    };

    const handleDeleteButtonClick: React.MouseEventHandler<HTMLImageElement> = async (e) => {
        e.stopPropagation();

        if (window.confirm('Biztosan törlöd a lépést?')) {
            showLoadingAlertMessage();

            const response = await networkCall(`/work-instructions/${ workInstruction.id }`, {
                method: 'DELETE'
            });

            const messageShowed = showDefaultErrorMessages(response);
            if(messageShowed) return;

            dispatch(clearAlertMessage());
            const alertMessageState: AlertMessageState = {
                alertMessageType: 'error',
                alertMessageText: AlertMessages.UnknownErrorTryAgain
            };

            if(response && response.status === 204) {
                dispatch(showAlertMessage({
                    alertMessageText: AlertMessages.DeletedSuccessfully
                }));
                onDelete();
                return;
            } else if (response?.status === 404) {
                const res: AxiosResponse<DefaultErrorResponse> = response;
    
                switch (res.data.code) {
    
                    case 6000:
                        alertMessageState.alertMessageText = 'A törlés sikertelen, a lépés nem létezik.';
                        break;
    
                }
            }

            dispatch(showAlertMessage(alertMessageState));
        }
    };

    const sendAudioInstruction = useDebouncedFunction(async (audioInstruction: string) => {
        const response = await networkCall(`/work-instructions/${ workInstruction.id }`, {
            method: 'PUT',
            body: {
                audioInstruction
            }
        });

        const messageShowed = showDefaultErrorMessages(response);
        if (messageShowed) return;

        dispatch(clearAlertMessage());
        const alertMessageState: AlertMessageState = {
            alertMessageType: 'error',
            alertMessageText: AlertMessages.UnknownErrorTryAgain
        };

        if (response?.status === 200) {
            dispatch(showAlertMessage({
                alertMessageText: AlertMessages.SavedSuccessfully
            }));
            
            onSavedWorkInstruction({
                ...workInstruction,
                audioInstruction
            });
            return;
        } else if (response?.status === 400) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {
                case 1000: {
                    const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                    if (_res.data.data && _res.data.data.length > 0) {
                        for (const error of _res.data.data) {
                            let alertMessage;
                            if (error.field === 'audioInstruction') alertMessage = 'Hangutasítás:';
                            alertMessageState.alertMessageText = `${ alertMessage } ${ error.errors.join(', ') }`;
                            break;
                        }
                    }
                    break;
                }
            }
        } else if (response?.status === 404) {
            const res: AxiosResponse<DefaultErrorResponse> = response;

            switch (res.data.code) {

                case 6000:
                    alertMessageState.alertMessageText = 'A lépés nem létezik.';
                    break;

            }
        }

        dispatch(showAlertMessage(alertMessageState));
    }, 1000);

    const handleAddIOTDeviceButtonClick = () => {
        setIOTDeviceModalType('newIOTDevice');
        toggleIOTDeviceModalOpen();
        setSelectedIOTDeviceId(null);
        setIOTDeviceModalKey(iotDeviceModalKey + 1);
    };

    const handleIOTDeviceListItemClick = (id: number) => {
        setIOTDeviceModalType('editIOTDevice');
        toggleIOTDeviceModalOpen();
        setSelectedIOTDeviceId(id);
    };

    const handleNewIOTDeviceAdd = (id: number) => {
        if (onNewIOTDeviceAdd) onNewIOTDeviceAdd();
        setSelectedIOTDeviceId(id);
        setIOTDeviceModalType('editIOTDevice');
    };

    const handleIOTDeviceDelete = () => {
        if (onIOTDeviceDelete) onIOTDeviceDelete();
        toggleIOTDeviceModalOpen();
        //setIOTDeviceModalKey(iotDeviceModalKey + 1);
    };

    const handleEditWorkInstructionDataClick = async () => {
        if (isWorkInsructionDataEditable) {
            
            showLoadingAlertMessage();

            const response = await networkCall(`/work-instructions/${ workInstruction.id }`, {
                method: 'PUT',
                body: {
                    title: workInsructionData.title,
                    description: workInsructionData.description
                }
            });
    
            const messageShowed = showDefaultErrorMessages(response);
            if (messageShowed) return;
    
            dispatch(clearAlertMessage());
            const alertMessageState: AlertMessageState = {
                alertMessageType: 'error',
                alertMessageText: AlertMessages.UnknownErrorTryAgain
            };
    
            if (response?.status === 200) {
                dispatch(showAlertMessage({
                    alertMessageText: AlertMessages.SavedSuccessfully
                }));
                toggleIsWorkInsructionDataEditable();
                return;
            } else if (response?.status === 400) {
                const res: AxiosResponse<DefaultErrorResponse> = response;
    
                switch (res.data.code) {
                    case 1000: {
                        const _res: AxiosResponse<DefaultErrorResponse<ValidationErrorResponseData[]>> = response;
                        if (_res.data.data && _res.data.data.length > 0) {
                            for (const error of _res.data.data) {
                                let alertMessage;
                                if (error.field === 'title') alertMessage = 'Cím:';
                                else if (error.field === 'description') alertMessage = 'Leírás:';
                                alertMessageState.alertMessageText = `${ alertMessage } ${ error.errors.join(', ') }`;
                                break;
                            }
                        }
                        break;
                    }
                }
            } else if (response?.status === 404) {
                const res: AxiosResponse<DefaultErrorResponse> = response;
    
                switch (res.data.code) {
    
                    case 6000:
                        alertMessageState.alertMessageText = 'A lépés nem létezik.';
                        break;
    
                }
            }
    
            dispatch(showAlertMessage(alertMessageState));
        } else {
            toggleIsWorkInsructionDataEditable();
        }
    };

    const padEndWithNBSP = useCallback((text: string, maxLength: number) => {
        const textWithSpaces = text.padEnd(maxLength, ' ');
        const result = [];

        let firstNotSpaceChar = false;
        for (let index = textWithSpaces.length - 1; index >= 0; index--) {
            if (firstNotSpaceChar) result.unshift(textWithSpaces[index]);
            else {
                if (textWithSpaces[index] === ' ') result.unshift(<>&nbsp;</>);
                else {
                    result.unshift(<>{textWithSpaces[index]}</>);
                    firstNotSpaceChar = true;
                }
            }
        }

        return result;
    }, []);

    const handleAudioInstructionChange = (value: string) => {
        setAudioInstruction(value);
        sendAudioInstruction(value);
    };
    
    return (
        <div
            className={`work-instruction animate__animated ${ isActive && 'active' } ${ (selectedWorkInstructionIndex !== null && selectedWorkInstructionIndex !== index && !isActive) && 'obscure' }`}
        >

            <video
                className="animation-video"
                style={{ zIndex: isActive ? 1 : 0 }}
                ref={animationVideoRef}
            >
                <source src={getVideoUrl('showHide.mp4')} type="video/mp4" />
            </video>

            {
                isActive && (
                    <>
                        <div className={`animate__animated ${ inputsContainerClass }`}>
                            {/* File uploaders */}
                            <div className="audio-file-upload-container">
                                <AudioUploader workInstruction={workInstruction} onSavedWorkInstruction={onSavedWorkInstruction} />
                            </div>
                            <div className="video-file-upload-container">
                                <VideoUploader workInstruction={workInstruction} onSavedWorkInstruction={onSavedWorkInstruction} />
                            </div>
                            <div className="image-file-upload-container">
                                <ImageUploader workInstruction={workInstruction} onSavedWorkInstruction={onSavedWorkInstruction} />
                            </div>
                            <div className="model-file-upload-container">
                                <ModelUploader workInstruction={workInstruction} onSavedWorkInstruction={onSavedWorkInstruction} />
                            </div>
                            <div className="pdf-file-upload-container">
                                <PDFUploader workInstruction={workInstruction} onSavedWorkInstruction={onSavedWorkInstruction} />
                            </div>
                            <div className="audio-instruction-container">
                                <Textarea
                                    id="audioInstruction"
                                    value={audioInstruction}
                                    label="Szöveg"
                                    onChange={handleAudioInstructionChange}
                                    placeholder="Hangutasítás szövege..."
                                    cols={35}
                                    rows={6}
                                />
                            </div>
                        </div>
                        
                        <div className={`animate__animated buttons-container ${ buttonsContainerClass }`}>
                            {/* Buttons */}
                            <img className='close-button' src={getImageUrl('close-button.png')} alt="close-button" title="Bezárás" onClick={(e) => { e.stopPropagation(); onClose(); }} />
                            <img
                                className='edit-button'
                                onClick={handleEditWorkInstructionDataClick}
                                src={getImageUrl('save-button.png')}
                                alt="edit-button"
                                title={isWorkInsructionDataEditable === true ? 'Cím és leírás mentése' : 'Cím és leírás módosítása'}
                            />
                            <div className="iot-button-container cursor-pointer" onClick={toggleIOTDevicesHandlingVisible}>
                                <span>iOT</span>
                                <img src={getImageUrl('iot-button.png')} alt="iot-button" title="iOT eszközök kezelése" />
                            </div>
                            {
                                iotDevicesHandlingVisible && (
                                    <>
                                        <img
                                            className='add-iot-device-button'
                                            src={getImageUrl('add-button.png')}
                                            alt="add-iot-device-button"
                                            title="Új iOT eszköz hozzáadása"
                                            onClick={handleAddIOTDeviceButtonClick}  
                                        />
                                        <div className='iot-devices-list-container'>
                                            <ol>
                                                {
                                                    workInstruction?.iotDevices?.map(iotDevice => (
                                                        <li key={iotDevice.id} onClick={() => handleIOTDeviceListItemClick(iotDevice.id)}>
                                                            {`${ iotDevice.name.substring(0, 24).trim() }${ iotDevice.name.length > 24 ? '...' : '' }`}
                                                        </li>
                                                    ))
                                                }
                                            </ol>
                                        </div>
                                    </>
                                )
                            }
                            <img className='delete-button' src={getImageUrl('delete-button.png')} alt="delete-button" title="Törlés" onClick={handleDeleteButtonClick} />
                        </div>
                    </>
                )
            }
            <div className="outer-container" onClick={handleWorkInstructionClick}>
                <div className="inner-container">

                    <div className="content text-center">
                        {
                            isWorkInsructionDataEditable ? (
                                <div>
                                    <input
                                        type='text'
                                        value={workInsructionData.title}
                                        onChange={(e) => {
                                            setWorkInsructionData({
                                                ...workInsructionData,
                                                title: e.target.value
                                            })
                                        }}
                                        maxLength={50}
                                    />
                                    <textarea
                                        value={workInsructionData.description}
                                        onChange={(e) => {
                                            setWorkInsructionData({
                                                ...workInsructionData,
                                                description: e.target.value
                                            })
                                        }}
                                        rows={5}
                                        maxLength={85}
                                    />
                                </div>
                            ) : (
                                <>
                                    <strong className='title'>{workInsructionData.title}</strong>
                                    <span className='description'>{workInsructionData.description}</span>
                                </>
                            )
                        }
                    </div>

                </div>
            </div>

            <IOTDeviceModal
                isModalOpen={isIOTDeviceModalOpen}
                onModalToggle={toggleIOTDeviceModalOpen}
                type={iotDeviceModalType}
                workInstructionId={workInstruction.id}
                onNewIOTDeviceAdd={handleNewIOTDeviceAdd}
                onIOTDeviceEdit={onIOTDeviceEdit}
                iotDevice={selectedIOTDevice}
                onIOTDeviceDelete={handleIOTDeviceDelete}
                key={iotDeviceModalKey}
            />
        </div>
    );
};